import React, { useMemo, useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	Chip,
	Divider,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	IconButton,
	Tooltip
} from '@mui/material';
import {
	Add as AddIcon,
	Remove as RemoveIcon,
	Edit as EditIcon,
	SwapVert as ReorderIcon,
	ExpandMore as ExpandMoreIcon,
	NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { getAssetViews } from '../../../../../services-px/configApi';

const ChangesSummary = ({ open, onClose, changes, onNavigateToChange }) => {
	// State for asset views (to resolve UUIDs to names)
	const [assetViews, setAssetViews] = useState([]);

	// Fetch asset views when dialog opens and there are changes
	useEffect(() => {
		if (open && changes.length > 0) {
			// Check if any changes involve asset images
			const hasAssetImageChanges = changes.some(
				(change) => change.description?.includes('Changed asset image')
			);
			if (hasAssetImageChanges) {
				getAssetViews()
					.then((views) => setAssetViews(views || []))
					.catch(() => setAssetViews([]));
			}
		}
	}, [open, changes]);

	// Helper to resolve asset view UUID to name
	const getAssetViewName = (uuid) => {
		if (!uuid || uuid === 'none') return '(none)';
		const view = assetViews.find((v) => v.id === uuid);
		return view?.name || uuid;
	};

	// Group changes by category and asset type for zones
	const groupedChanges = useMemo(() => {
		const groups = {};
		changes.forEach(change => {
			// For Zone category, group by asset type instead
			if (change.category === 'Zone' && change.details?.assetTypes) {
				const key = change.details.assetTypes;
				if (!groups[key]) {
					groups[key] = [];
				}
				groups[key].push(change);
			} else {
				if (!groups[change.category]) {
					groups[change.category] = [];
				}
				groups[change.category].push(change);
			}
		});
		return groups;
	}, [changes]);

	// Get icon for change type
	const getChangeIcon = (type) => {
		switch (type) {
			case 'added':
				return <AddIcon fontSize="small" color="success" />;
			case 'removed':
				return <RemoveIcon fontSize="small" color="error" />;
			case 'modified':
				return <EditIcon fontSize="small" color="primary" />;
			case 'reordered':
				return <ReorderIcon fontSize="small" color="info" />;
			default:
				return <EditIcon fontSize="small" />;
		}
	};

	// Get color for change type
	const getChangeColor = (type) => {
		switch (type) {
			case 'added':
				return 'success';
			case 'removed':
				return 'error';
			case 'modified':
				return 'primary';
			case 'reordered':
				return 'info';
			default:
				return 'default';
		}
	};

	// Format change details
	const formatDetails = (change) => {
		const details = change.details;
		if (!details) return null;

		// Check if this is an asset image change (to resolve UUIDs to names)
		const isAssetImageChange = change.description?.includes('Changed asset image');

		return (
			<>
				{/* Handle old/new value changes */}
				{details.oldValue !== undefined && details.newValue !== undefined && (
					<Box sx={{ mt: 1, ml: 4 }}>
						<Typography variant="body2" color="text.secondary" component="div">
							<span style={{ textDecoration: 'line-through', color: '#d32f2f' }}>
								{isAssetImageChange ? getAssetViewName(details.oldValue) : (details.oldValue ?? '(none)')}
							</span>
							{' → '}
							<span style={{ color: '#2e7d32', fontWeight: 'bold' }}>
								{isAssetImageChange ? getAssetViewName(details.newValue) : (details.newValue ?? '(none)')}
							</span>
						</Typography>
					</Box>
				)}

				{/* Handle position changes */}
				{details.oldPosition !== undefined && details.newPosition !== undefined && (
					<Box sx={{ mt: 1, mb: 0, pb: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
						{details.gridMove && details.gridMove.from && details.gridMove.to ? (
							<Box sx={{ mb: 0, pb: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							<Box
								sx={{
									display: 'inline-block',
									'& svg': {
										display: 'block',
										width: '100%',
										height: 'auto',
										transform: 'scale(0.65)',
										transformOrigin: 'center center'
									}
								}}
							>
							<div dangerouslySetInnerHTML={{ __html: details.gridMove.visualization }} />
						</Box>
							<Box sx={{ mt: '-24px', mb: 0, pb: 0, display: 'flex', gap: 2, fontSize: '0.75rem', justifyContent: 'center' }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
										<Box sx={{ width: 12, height: 12, bgcolor: 'rgba(255, 152, 0, 0.6)', border: '1px solid rgba(255, 152, 0, 0.8)', borderRadius: 0.5 }} />
										<Typography variant="caption">From (F)</Typography>
									</Box>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
										<Box sx={{ width: 12, height: 12, bgcolor: 'rgba(76, 175, 80, 0.6)', border: '1px solid rgba(76, 175, 80, 0.8)', borderRadius: 0.5 }} />
										<Typography variant="caption">To (T)</Typography>
									</Box>
								</Box>
							</Box>
						) : (
							<Typography variant="body2" color="text.secondary">
								Position: {details.oldPosition} → {details.newPosition}
							</Typography>
						)}
					</Box>
				)}

				{/* Handle added/removed names (for inspection types, etc) */}
				{(details.addedNames?.length > 0 || details.removedNames?.length > 0) && (
					<Box sx={{ mt: 1, ml: 4 }}>
						{details.removedNames?.length > 0 && (
							<Typography variant="body2" color="text.secondary" component="div">
								<span style={{ color: '#d32f2f' }}>Removed: </span>
								<span style={{ textDecoration: 'line-through' }}>
									{details.removedNames.join(', ')}
								</span>
							</Typography>
						)}
						{details.addedNames?.length > 0 && (
							<Typography variant="body2" color="text.secondary" component="div">
								<span style={{ color: '#2e7d32' }}>Added: </span>
								<span style={{ fontWeight: 'bold' }}>
									{details.addedNames.join(', ')}
								</span>
							</Typography>
						)}
					</Box>
				)}

				{/* Handle count changes */}
				{details.oldCount !== undefined && details.newCount !== undefined && !details.addedNames && !details.removedNames && (
					<Box sx={{ mt: 1, ml: 4 }}>
						<Typography variant="body2" color="text.secondary">
							Count: {details.oldCount} → {details.newCount}
						</Typography>
					</Box>
				)}

				{/* Handle field type */}
				{details.fieldType && (
					<Box sx={{ mt: 0.5, ml: 4 }}>
						<Chip 
							label={details.fieldType} 
							size="small" 
							variant="outlined"
							sx={{ fontSize: '0.7rem' }}
						/>
					</Box>
				)}

			</>
		);
	};

	const categoryOrder = [
		'Zone Layout',
		'Components',
		'Inspection Type',
		'Inspection Property',
		'Form Field'
	];

	const sortedCategories = Object.keys(groupedChanges).sort((a, b) => {
		const indexA = categoryOrder.indexOf(a);
		const indexB = categoryOrder.indexOf(b);
		if (indexA === -1 && indexB === -1) return a.localeCompare(b);
		if (indexA === -1) return 1;
		if (indexB === -1) return -1;
		return indexA - indexB;
	});

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			aria-labelledby="changes-dialog-title"
		>
			<DialogTitle id="changes-dialog-title">
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="h6">Configuration Changes</Typography>
					<Chip 
						label={`${changes.length} ${changes.length === 1 ? 'change' : 'changes'}`}
						color="primary"
						size="small"
					/>
				</Box>
			</DialogTitle>
			
			<DialogContent>
				{changes.length === 0 ? (
					<Box sx={{ textAlign: 'center', py: 4 }}>
						<Typography variant="body1" color="text.secondary">
							No changes detected
						</Typography>
					</Box>
				) : (
					<Box>
						{sortedCategories.map((category) => (
							<Accordion 
								key={category} 
								defaultExpanded
								elevation={0}
								sx={{ 
									'&:before': { display: 'none' },
									border: '1px solid',
									borderColor: 'divider',
									mb: 1
								}}
							>
									<AccordionSummary 
										expandIcon={<ExpandMoreIcon />}
										sx={{ 
											backgroundColor: 'action.hover',
											'&:hover': { backgroundColor: 'action.selected' },
											minHeight: 40,
											'& .MuiAccordionSummary-content': { my: 0.5 }
										}}
									>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
										<Typography 
											variant="subtitle2" 
											fontWeight="bold"
											sx={{ textTransform: categoryOrder.includes(category) ? 'none' : 'capitalize' }}
										>
											{category}
										</Typography>
										{categoryOrder.includes(category) && (
											<Chip 
												label={groupedChanges[category].length}
												size="small"
												variant="outlined"
											/>
										)}
									</Box>
									</AccordionSummary>
								<AccordionDetails sx={{ p: 0 }}>
									<List dense>
										{groupedChanges[category].map((change, index) => (
											<React.Fragment key={index}>
												<ListItem
													sx={{
														alignItems: 'flex-start',
														py: 1.5,
														pr: 7,
														'&:hover': { backgroundColor: 'action.hover' }
													}}
													secondaryAction={
														<Tooltip title="Go to change location">
															<IconButton
																edge="end"
																size="small"
																color="primary"
																onClick={() => onNavigateToChange && onNavigateToChange(change)}
															>
																<NavigateNextIcon />
															</IconButton>
														</Tooltip>
													}
												>
													<ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
														{getChangeIcon(change.type)}
													</ListItemIcon>
													<ListItemText
														primary={change.description}
														primaryTypographyProps={{
															variant: 'body2',
															color: 'text.primary',
															sx: {
																wordBreak: 'break-word',
																overflowWrap: 'break-word'
															}
														}}
														secondary={formatDetails(change)}
														secondaryTypographyProps={{
															component: 'div'
														}}
													/>
												</ListItem>
												{index < groupedChanges[category].length - 1 && (
													<Divider component="li" />
												)}
											</React.Fragment>
										))}
									</List>
								</AccordionDetails>
							</Accordion>
						))}
					</Box>
				)}
			</DialogContent>
			
			<DialogActions>
				<Button onClick={onClose} variant="contained">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ChangesSummary;
